import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  FileText,
  FlaskConical,
  MessageCircle,
  Briefcase,
  Users,
  Clock,
  ExternalLink,
} from "lucide-react";
import api from "../../api/client";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import LoadingSpinner from "../../components/LoadingSpinner";

const TYPE_CONFIG = {
  meeting: {
    icon: Calendar,
    color: "text-brand-violet dark:text-brand-violet-on-dark",
    bg: "bg-brand-violet/10",
    label: "Meeting",
  },
  document_request: {
    icon: FileText,
    color: "text-brand-violet dark:text-brand-violet-on-dark",
    bg: "bg-brand-violet/10",
    label: "Document request",
  },
  assessment: {
    icon: FlaskConical,
    color: "text-brand-violet dark:text-brand-violet-on-dark",
    bg: "bg-brand-violet/10",
    label: "Assessment",
  },
  info: {
    icon: MessageCircle,
    color: "text-gray-400",
    bg: "bg-gray-400/10",
    label: "Information",
  },
  job_position: {
    icon: Briefcase,
    color: "text-gray-400",
    bg: "bg-gray-400/10",
    label: "Job position",
  },
};

function SenderBadge({ senderType, senderName }) {
  return (
    <span
      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
        senderType === "agency"
          ? "bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400"
          : "bg-brand-violet/10 text-brand-violet dark:text-brand-violet-on-dark"
      }`}
    >
      {senderType === "agency" ? "Agency" : "Employer"} ·{" "}
      {senderName ?? "Unknown"}
    </span>
  );
}

function MeetingItem({ item, action, onRespond }) {
  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleString("en-SE", {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : null;

  return (
    <div className="mt-3 flex flex-col gap-3">
      {action.meeting_at && (
        <p className="text-sm text-brand-violet dark:text-brand-violet-on-dark font-medium flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          {formatDate(action.meeting_at)}
          {action.duration_minutes && ` · ${action.duration_minutes} min`}
        </p>
      )}
      {action.attendees?.length > 0 && (
        <p className="text-xs text-gray-400 flex items-center gap-1">
          <Users className="w-3 h-3" />
          {action.attendees.join(", ")}
        </p>
      )}
      {action.description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
          {action.description}
        </p>
      )}
      {!item.response && (
        <div className="flex gap-2">
          <button
            onClick={() => onRespond(item.id, "accepted")}
            className="bg-brand-violet text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition"
          >
            Accept
          </button>
          <button
            onClick={() => onRespond(item.id, "declined")}
            className="border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition"
          >
            Decline
          </button>
        </div>
      )}
      {item.response && (
        <span
          className={`text-xs font-medium px-2.5 py-1 rounded-full w-fit ${
            item.response === "accepted"
              ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {item.response === "accepted" ? "✓ Accepted" : "✗ Declined"}
        </span>
      )}
    </div>
  );
}

function DocumentRequestItem({ item, action, onRespond }) {
  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleString("en-SE", {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : null;

  return (
    <div className="mt-3 flex flex-col gap-3">
      {action.description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
          {action.description}
        </p>
      )}
      {action.deadline && (
        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Deadline: {formatDate(action.deadline)}
        </p>
      )}
      {action.guidelines && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
          {action.guidelines}
        </p>
      )}
      {!item.response && (
        <button
          onClick={() => onRespond(item.id, "submitted")}
          className="bg-brand-violet text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition w-fit"
        >
          Mark as submitted
        </button>
      )}
      {item.response === "submitted" && (
        <span className="text-xs font-medium px-2.5 py-1 rounded-full w-fit bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">
          ✓ Submitted
        </span>
      )}
    </div>
  );
}

function AssessmentItem({ item, action, onRespond }) {
  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleString("en-SE", {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : null;

  return (
    <div className="mt-3 flex flex-col gap-3">
      {action.description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
          {action.description}
        </p>
      )}
      {action.deadline && (
        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Deadline: {formatDate(action.deadline)}
        </p>
      )}
      {action.guidelines && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
          {action.guidelines}
        </p>
      )}
      <div className="flex gap-2 flex-wrap">
        {action.url && (
          <a
            href={action.url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 bg-brand-violet text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open assessment
          </a>
        )}
        {!item.response && (
          <button
            onClick={() => onRespond(item.id, "submitted")}
            className="border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition"
          >
            Mark as submitted
          </button>
        )}
      </div>
      {item.response === "submitted" && (
        <span className="text-xs font-medium px-2.5 py-1 rounded-full w-fit bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">
          ✓ Submitted
        </span>
      )}
    </div>
  );
}

function JobPositionItem({ item, onImport, onDismiss, importing }) {
  return (
    <div className="mt-3 flex flex-col gap-3">
      {item.job_position?.message && (
        <div className="bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-xl px-4 py-3">
          <p className="text-xs font-medium text-brand-violet dark:text-brand-violet-on-dark mb-1">
            Message from employer
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            {item.job_position.message}
          </p>
        </div>
      )}
      {item.job_position && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          {item.job_position.location && (
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Location</p>
              <p className="text-gray-700 dark:text-gray-300">
                {item.job_position.location}
                {item.job_position.is_remote && " (Remote)"}
              </p>
            </div>
          )}
          {item.job_position.salary_min && (
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Salary</p>
              <p className="text-gray-700 dark:text-gray-300">
                {item.job_position.salary_min.toLocaleString()}–
                {item.job_position.salary_max?.toLocaleString()}{" "}
                {item.job_position.salary_currency}
              </p>
            </div>
          )}
        </div>
      )}
      {item.job_position?.description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-3">
          {item.job_position.description}
        </p>
      )}
      {item.job_position?.process_steps?.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
            Expected hiring process
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {item.job_position.process_steps.map((step, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-xs bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 px-2.5 py-1 rounded-full">
                  {idx + 1}. {step}
                </span>
                {idx < item.job_position.process_steps.length - 1 && (
                  <svg
                    className="w-3 h-3 text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {!item.is_imported_as_application && item.response !== "dismissed" && (
        <div className="flex gap-2">
          <button
            onClick={onImport}
            disabled={importing}
            className="bg-brand-violet text-white px-5 py-2 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
          >
            {importing ? "Importing..." : "Import as application"}
          </button>
          <button
            onClick={onDismiss}
            className="border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 px-5 py-2 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition"
          >
            Dismiss
          </button>
        </div>
      )}
      {item.is_imported_as_application && (
        <span className="text-xs font-medium px-2.5 py-1 rounded-full w-fit bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">
          ✓ Imported
        </span>
      )}
      {item.response === "dismissed" && (
        <span className="text-xs font-medium px-2.5 py-1 rounded-full w-fit bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
          Dismissed
        </span>
      )}
    </div>
  );
}

export default function Inbox() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");

  const fetchInbox = async () => {
    try {
      const { data } = await api.get("/arena/inbox");
      setItems(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Joborigo — Inbox";
  }, []);
  useEffect(() => {
    fetchInbox();
  }, []);

  const handleMarkRead = async (id) => {
    await api.patch(`/arena/inbox/${id}/read`);
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, is_read: true } : i)),
    );
  };

  const handleImport = async (id) => {
    setImporting(id);
    try {
      await api.post(`/arena/inbox/${id}/import`);
      await fetchInbox();
      navigate("/app/dashboard");
    } catch (err) {
      alert(err.response?.data?.detail ?? err.message);
    } finally {
      setImporting(null);
    }
  };

  const handleDismiss = async (id) => {
    try {
      await api.patch(`/arena/inbox/${id}/respond`, { response: "dismissed" });
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, response: "dismissed" } : i)),
      );
    } catch (err) {
      alert(err.response?.data?.detail ?? err.message);
    }
  };

  const handleRespond = async (id, response) => {
    try {
      await api.patch(`/arena/inbox/${id}/respond`, { response });
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, response } : i)),
      );
      await api.patch(`/arena/inbox/${id}/read`);
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, is_read: true } : i)),
      );
    } catch (err) {
      alert(err.response?.data?.detail ?? err.message);
    }
  };

  const FILTERS = [
    "all",
    "meeting",
    "document_request",
    "assessment",
    "info",
    "job_position",
  ];

  const filtered = items
    .filter((i) => filter === "all" || i.type === filter)
    .sort((a, b) => {
      if (sort === "unread")
        return a.is_read === b.is_read ? 0 : a.is_read ? 1 : -1;
      if (sort === "oldest")
        return new Date(a.created_at) - new Date(b.created_at);
      return new Date(b.created_at) - new Date(a.created_at);
    });

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <NavBar currentPage="inbox" />

      <main className="flex-1 max-w-3xl mx-auto px-6 py-10 w-full">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-1">Inbox</h1>
          <p className="text-gray-400 text-sm">
            All correspondence and job positions in one place
          </p>
        </div>

        {/* Filter + Sort */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  filter === f
                    ? "bg-brand-violet text-white"
                    : "bg-white dark:bg-brand-dark-surface border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-brand-violet hover:text-brand-violet dark:hover:text-brand-violet-on-dark"
                }`}
              >
                {f === "all" ? "All" : (TYPE_CONFIG[f]?.label ?? f)}
              </button>
            ))}
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface rounded-lg px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-violet"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="unread">Unread first</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-20">
            <LoadingSpinner />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-dashed border-gray-200 dark:border-white/10 p-16 text-center">
            <div className="w-12 h-12 bg-brand-violet-tint dark:bg-brand-violet/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-brand-violet dark:text-brand-violet-on-dark"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
            </div>
            <p className="text-gray-400 text-sm font-medium mb-1">
              No messages yet
            </p>
            <p className="text-gray-400 text-xs">
              Opt in to the candidate arena in your settings to start receiving
              messages.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {filtered.map((item) => {
              const config = TYPE_CONFIG[item.type] ?? TYPE_CONFIG.info;
              const IconComponent = config.icon;

              return (
                <div
                  key={item.id}
                  className={`bg-white dark:bg-brand-dark-surface rounded-2xl border transition cursor-pointer ${
                    item.is_read
                      ? "border-gray-100 dark:border-white/10"
                      : "border-brand-violet/30 dark:border-brand-violet-on-dark/30"
                  }`}
                  onClick={() => {
                    if (!item.is_read) handleMarkRead(item.id);
                    if (item.application_id)
                      navigate(
                        `/app/dashboard?open=${item.application_id}&tab=Status`,
                      );
                  }}
                >
                  <div className="p-5">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex items-start gap-3">
                        {!item.is_read && (
                          <div className="w-2 h-2 rounded-full bg-brand-violet flex-shrink-0 mt-2" />
                        )}
                        <div
                          className={`w-9 h-9 rounded-xl ${config.bg} flex items-center justify-center flex-shrink-0`}
                        >
                          <IconComponent
                            className={`w-5 h-5 ${config.color}`}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-gray-900 dark:text-white text-sm">
                              {item.type === "job_position"
                                ? (item.job_position?.title ?? "Job position")
                                : (item.action?.title ?? config.label)}
                            </p>
                            <span
                              className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}
                            >
                              {config.label}
                            </span>
                          </div>
                          <SenderBadge
                            senderType={item.sender_type}
                            senderName={
                              item.sender_name ??
                              item.job_position?.company_name
                            }
                          />
                        </div>
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {new Date(item.created_at).toLocaleDateString("en-SE", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                    {/* Item type content */}
                    {item.type === "job_position" && (
                      <JobPositionItem
                        item={item}
                        onImport={(e) => {
                          e?.stopPropagation?.();
                          handleImport(item.id);
                        }}
                        onDismiss={(e) => {
                          e?.stopPropagation?.();
                          handleDismiss(item.id);
                        }}
                        importing={importing === item.id}
                      />
                    )}
                    {item.type === "meeting" && item.action && (
                      <MeetingItem
                        item={item}
                        action={item.action}
                        onRespond={handleRespond}
                      />
                    )}
                    {item.type === "document_request" && item.action && (
                      <DocumentRequestItem
                        item={item}
                        action={item.action}
                        onRespond={handleRespond}
                      />
                    )}
                    {item.type === "assessment" && item.action && (
                      <AssessmentItem
                        item={item}
                        action={item.action}
                        onRespond={handleRespond}
                      />
                    )}
                    {item.type === "info" && item.action && (
                      <div className="mt-3">
                        {item.action.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                            {item.action.description}
                          </p>
                        )}
                        {item.action.url && (
                          <a
                            href={item.action.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-brand-violet dark:text-brand-violet-on-dark hover:underline mt-2 inline-block"
                          >
                            Open link →
                          </a>
                        )}
                      </div>
                    )}
                    {/* Status change with no action attached. Without this the
                        card renders empty, since the note lives on the status
                        rather than on the inbox row. */}
                    {item.type === "info" &&
                      !item.action &&
                      (item.status_label || item.note) && (
                        <div className="mt-3">
                          {item.status_label && (
                            <span className="text-xs font-medium text-brand-violet dark:text-brand-violet-on-dark">
                              Status: {item.status_label}
                            </span>
                          )}
                          {item.note && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mt-1">
                              {item.note}
                            </p>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
